import Link from 'next/link'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUser } from 'web/hooks/use-user'
import { LanguageSelector } from 'web/components/language-selector'
import { Row } from '../layout/row'
import { firebaseLogin } from 'web/lib/firebase/users'

// Logo Predita - ícone quadrado preto com símbolo
function PreditaLogoSvg() {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2">
      <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
        <rect width="28" height="28" rx="4" fill="#000" />
        <path
          d="M8 20L14 8L20 20H17L14 14L11 20H8Z"
          fill="white"
        />
      </svg>
      <span
        className="text-[22px] font-semibold tracking-tight text-[#000] dark:text-white"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}
      >
        {t('brand.name')}
      </span>
    </div>
  )
}

// Info icon - Polymarket style: FILLED blue circle with white "i"
function InfoIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="#2563EB" />
      <text
        x="8"
        y="12"
        textAnchor="middle"
        fill="white"
        fontSize="11"
        fontWeight="600"
        fontFamily="Arial"
      >
        i
      </text>
    </svg>
  )
}

// Search icon - simple magnifying glass
function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#858585" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  )
}

// Menu icon - 3 horizontal lines
function MenuIcon() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 12h18" />
      <path d="M3 6h18" />
      <path d="M3 18h18" />
    </svg>
  )
}

// Trending icon - chart going up (like Polymarket)
function TrendingIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  )
}

export function TopHeader() {
  const { t } = useTranslation()
  const user = useUser()
  const router = useRouter()
  const [searchQuery, setSearchQuery] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      router.push(`/browse?q=${encodeURIComponent(searchQuery)}`)
    }
  }

  return (
    <nav className="sticky inset-x-0 top-0 z-30 w-full bg-white dark:bg-[#0d0d0d]">
      {/* Bottom border - exactly like Polymarket */}
      <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-[#e8e8e8] dark:bg-[#2d2d2d]" />

      {/* Main header content */}
      <div
        className="mx-auto flex w-full items-center justify-between gap-6 px-4 py-3 lg:px-6"
        style={{ maxWidth: '1350px' }}
      >
        {/* Left: Logo */}
        <div className="flex shrink-0 items-center">
          <Link href={user ? '/home' : '/'} aria-label={t('brand.logoAlt')} className="flex items-center">
            <PreditaLogoSvg />
          </Link>
        </div>

        {/* Center: Search bar - EXACT Polymarket style */}
        <div className="hidden flex-1 md:flex">
          <form onSubmit={handleSearch} className="w-full max-w-[580px]">
            <div className="relative flex items-center">
              <div className="absolute left-4">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                style={{ borderRadius: '24px' }}
                className="h-[42px] w-full border-0 bg-[#f0f0f0] pl-11 pr-10 text-[15px] text-[#000] placeholder-[#858585] outline-none transition-colors focus:bg-[#e8e8e8] dark:bg-[#1a1a1a] dark:text-white dark:placeholder-[#666]"
              />
              {/* Keyboard shortcut "/" */}
              <div
                style={{ borderRadius: '4px' }}
                className="absolute right-3 border border-[#d0d0d0] bg-white px-[6px] py-[2px] text-[12px] text-[#999] dark:border-[#444] dark:bg-[#222]"
              >
                /
              </div>
            </div>
          </form>
        </div>

        {/* Right: Actions - EXACT Polymarket style */}
        <Row className="shrink-0 items-center gap-0">
          {/* How it works - info icon filled blue + black text */}
          <Link
            href="/about"
            className="hidden items-center gap-2 px-3 py-2 text-[15px] font-medium text-[#000] transition-opacity hover:opacity-70 md:flex dark:text-white"
          >
            <InfoIcon />
            <span>{t('nav.howItWorks')}</span>
          </Link>

          {user ? (
            <Link
              href="/home"
              style={{ borderRadius: '8px' }}
              className="ml-2 bg-[#2563EB] px-4 py-[10px] text-[15px] font-semibold text-white transition-colors hover:bg-[#1d4ed8]"
            >
              {t('nav.dashboard')}
            </Link>
          ) : (
            <>
              {/* Log In - BLUE text like Polymarket */}
              <button
                onClick={() => firebaseLogin()}
                className="hidden px-4 py-2 text-[15px] font-semibold text-[#2563EB] transition-opacity hover:opacity-70 md:block"
              >
                {t('nav.login')}
              </button>
              {/* Sign Up - blue button */}
              <button
                onClick={() => firebaseLogin()}
                style={{ borderRadius: '8px' }}
                className="ml-2 bg-[#2563EB] px-4 py-[10px] text-[15px] font-semibold text-white transition-colors hover:bg-[#1d4ed8]"
              >
                {t('nav.signup')}
              </button>
            </>
          )}

          {/* Language selector */}
          <LanguageSelector />
          {/* Hamburger menu */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="ml-3 flex h-10 w-10 items-center justify-center text-[#000] transition-opacity hover:opacity-70 dark:text-white"
          >
            <MenuIcon />
          </button>
        </Row>
      </div>

      {/* Mobile menu dropdown */}
      {mobileMenuOpen && (
        <div className="border-t border-[#e8e8e8] bg-white px-4 py-4 dark:border-[#2d2d2d] dark:bg-[#0d0d0d] md:hidden">
          <form onSubmit={handleSearch} className="mb-4">
            <div className="relative flex items-center">
              <div className="absolute left-4">
                <SearchIcon />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t('nav.searchPlaceholder')}
                style={{ borderRadius: '24px' }}
                className="h-[42px] w-full border-0 bg-[#f0f0f0] pl-11 pr-4 text-[15px] text-[#000] placeholder-[#858585] outline-none dark:bg-[#1a1a1a] dark:text-white"
              />
            </div>
          </form>
          <Link
            href="/about"
            className="flex items-center gap-2 py-2 text-[15px] font-medium text-[#000] dark:text-white"
          >
            <InfoIcon />
            {t('nav.howItWorks')}
          </Link>
          {!user && (
            <button
              onClick={() => firebaseLogin()}
              className="mt-2 w-full py-2 text-left text-[15px] font-semibold text-[#2563EB]"
            >
              {t('nav.login')}
            </button>
          )}
        </div>
      )}
    </nav>
  )
}

// Category navigation bar - EXACT Polymarket style
export function CategoryNav(props: { className?: string }) {
  const { t } = useTranslation()
  const router = useRouter()
  const currentPath = router.asPath

  const mainCategories = [
    { name: t('nav.trending'), href: '/browse', icon: <TrendingIcon />, isTrending: true },
    { name: t('nav.breaking'), href: '/browse?s=newest', icon: null },
    { name: t('nav.new'), href: '/browse?s=newest', icon: null },
  ]

  const categories = [
    { name: t('categories.politics'), href: '/browse?t=politics' },
    { name: t('categories.sports'), href: '/browse?t=sports' },
    { name: t('categories.crypto'), href: '/browse?t=crypto' },
    { name: t('categories.finance'), href: '/browse?t=finance' },
    { name: t('categories.geopolitics'), href: '/browse?t=geopolitics' },
    { name: t('categories.earnings'), href: '/browse?t=earnings' },
    { name: t('categories.tech'), href: '/browse?t=tech' },
    { name: t('categories.culture'), href: '/browse?t=culture' },
    { name: t('categories.world'), href: '/browse?t=world' },
    { name: t('categories.economy'), href: '/browse?t=economy' },
    { name: t('categories.elections'), href: '/browse?t=elections' },
  ]

  return (
    <div
      className={clsx(
        'sticky top-[52px] z-20 w-full border-b border-[#e5e5e5] bg-white dark:border-[#2d2d2d] dark:bg-[#0d0d0d]',
        props.className
      )}
    >
      <div
        className="scrollbar-hide mx-auto flex items-center gap-0 overflow-x-auto px-4 lg:px-6"
        style={{ maxWidth: '1350px' }}
      >
        {/* Main categories (Trending, Breaking, New) */}
        {mainCategories.map((category) => (
          <Link
            key={category.name}
            href={category.href}
            className={clsx(
              'flex shrink-0 items-center gap-1 px-3 py-3 text-[14px] font-medium transition-colors',
              currentPath === category.href
                ? 'text-[#0d0d0d] dark:text-white'
                : 'text-[#666] hover:text-[#0d0d0d] dark:text-[#888] dark:hover:text-white'
            )}
          >
            {category.icon && <span>{category.icon}</span>}
            {category.name}
          </Link>
        ))}

        {/* Separator - exactly like Polymarket */}
        <div className="mx-2 h-5 w-px bg-[#e5e5e5] dark:bg-[#2d2d2d]" />

        {/* Other categories */}
        {categories.map((category) => (
          <Link
            key={category.name}
            href={category.href}
            className={clsx(
              'shrink-0 px-3 py-3 text-[14px] font-medium transition-colors',
              currentPath === category.href
                ? 'text-[#0d0d0d] dark:text-white'
                : 'text-[#666] hover:text-[#0d0d0d] dark:text-[#888] dark:hover:text-white'
            )}
          >
            {category.name}
          </Link>
        ))}

        {/* More dropdown */}
        <button className="flex shrink-0 items-center gap-1 px-3 py-3 text-[14px] font-medium text-[#666] transition-colors hover:text-[#0d0d0d] dark:text-[#888] dark:hover:text-white">
          {t('common.more')}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      </div>
    </div>
  )
}

// Combined header with category nav
export function PolymarketStyleHeader() {
  return (
    <>
      <TopHeader />
      <CategoryNav />
    </>
  )
}

